
import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Textarea } from './ui/Input';
import { Select } from './ui/Select';
import { NewObraData, UserRole } from '../types';
import { useCompanies } from '../hooks/useCompanies';
import { useUsers } from '../hooks/useUsers';

interface AddObraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NewObraData) => void;
}

const initialFormData: NewObraData = {
  companyId: '',
  personInCharge: '',
  serviceDescription: '',
};

export const AddObraModal: React.FC<AddObraModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<NewObraData>(initialFormData);
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<{ value: string; label: string }[]>([]);
  const { users } = useUsers();
  const { companies } = useCompanies();

  useEffect(() => {
    // Populate company dropdown
    const sortedCompanies = [...companies].sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim(), 'pt-BR', { sensitivity: 'base' }));
    const options = sortedCompanies.map(company => ({
      value: company.id,
      label: `${company.name} - ${company.address.city}/${company.address.state}`,
    }));
    setCompanyOptions(options);

    // Populate employee (encarregado) dropdown strictly A-Z
    const empOptions = users
      .filter(u => u.role === UserRole.EMPLOYEE)
      .sort((a, b) => (a.name || '').trim().localeCompare((b.name || '').trim(), 'pt-BR', { sensitivity: 'base' }))
      .map(user => ({
        value: user.name, 
        label: user.name.toUpperCase(),
      }));
    setEmployeeOptions(empOptions);

  }, [users, companies]);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData); 
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.companyId || !formData.personInCharge || !formData.serviceDescription) {
      alert('Todos os campos são obrigatórios.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Adicionar Nova Obra" size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Empresa*"
          id="companyId"
          name="companyId"
          value={formData.companyId}
          onChange={handleChange}
          options={companyOptions}
          placeholderOption="Selecione uma empresa"
          required
        />
        <Select
          label="Encarregado*"
          id="personInCharge"
          name="personInCharge"
          value={formData.personInCharge}
          onChange={handleChange}
          options={employeeOptions}
          placeholderOption="Selecione um encarregado"
          required
        />
        <Textarea
          label="Descrição do Serviço*"
          id="serviceDescription"
          name="serviceDescription"
          value={formData.serviceDescription}
          onChange={handleChange}
          rows={4}
          required
        />
        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            Criar Obra
          </Button>
        </div>
      </form>
    </Modal>
  );
};
